import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Anchor, ArrowRight } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

export default function Equipment() {
  const { assets, resolveMediaUrl, products } = useBrand();

  const equipment = [
    {
      id: "endurance",
      title: "THE OWS EXPEDITION CX2",
      description: "Our signature 32-inch wide expedition board built for sustained open water distance and all-terrain exploration.",
      image: assets.boardImage,
      link: "/expedition-cx2"
    },
    ...products.filter(p => p.category === 'Equipment').map(p => ({
      id: p.id,
      title: p.name,
      description: p.description,
      image: p.image,
      link: `/equipment/${p.id}`
    }))
  ];

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Equipment | Open Water Syndicate</title>
        <meta name="description" content="Explore the arsenal of OWS equipment designed for true exploration." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 text-ows-teal mb-6"
          >
            <Anchor className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">OWS Arsenal</span>
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-display font-light uppercase tracking-widest text-ows-dark mb-8"
          >
            Equipment
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-ows-dark/60 text-xl font-light tracking-wide max-w-2xl mx-auto"
          >
            The tools of our syndicate. Built for survival, designed for the uncharted.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {equipment.map((item, index) => (
            <motion.div 
              key={item.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + (index * 0.1) }}
              className="bg-white rounded-[2rem] overflow-hidden border border-ows-teal/5 hover:border-ows-teal/30 hover:shadow-2xl transition-all group"
            >
              <Link to={item.link} className="block relative aspect-[4/3] bg-ows-sand/10 overflow-hidden">
                <img 
                  src={resolveMediaUrl(item.image) || "https://picsum.photos/seed/ows-gear/800/600"} 
                  alt={item.title} 
                  className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-700" 
                  referrerPolicy="no-referrer"
                />
              </Link>
              <div className="p-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-display font-light uppercase tracking-widest text-ows-dark group-hover:text-ows-teal transition-colors">{item.title}</h3>
                  {item.title.toLowerCase().includes('c2') && (
                    <span className="bg-ows-orange text-white px-2 py-1 text-[8px] font-bold tracking-widest uppercase rounded-full">Pre-Order</span>
                  )}
                </div>
                <p className="text-ows-dark/60 text-sm leading-relaxed mb-8">{item.description}</p>
                <Link to={item.link} className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-ows-teal">
                  Explore Blueprint <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
